import * as Sentry from '@sentry/nextjs';
import { SendTransactionParameters } from 'viem';

import { ClientInputError } from '@/lib/errors/client';

import { SUPPORTED_CHAINS_BY_CHAIN_ID } from '@/constants/chain';

import type { FillOrderArgs } from '@/utils/0x.types';
import { EthereumHash } from '@/utils/models';
import { sleep } from '@/utils/sleep';

export const fillOrder = async ({
  transaction,
  walletClient,
  connectorId,
  chainId,
  txOptions: {
    blobs,
    kzg,
    maxFeePerBlobGas,
    maxFeePerGas,
    maxPriorityFeePerGas,
    type,
    ...txOptions
  } = {},
}: FillOrderArgs): Promise<EthereumHash> => {
  return Sentry.startSpan(
    {
      name: 'fillOrder',
      op: 'submitTransaction',
    },
    async (span) => {
      // set attributes on the span
      span.setAttribute('user.account.address', walletClient?.account?.address ?? 'no_address');
      span.setAttribute('user.account.chainID', chainId);

      // validate
      if (!transaction) throw new ClientInputError('No transaction data provided!');
      if (!walletClient || !walletClient.account)
        throw new ClientInputError('No account provided!');
      if (!chainId) throw new ClientInputError('No chainId provided!');

      const { to, data, gas, value } = transaction;

      const txParams: Omit<SendTransactionParameters, 'gasPrice'> = {
        account: walletClient.account,
        gas: !!gas ? BigInt(gas) : undefined,
        ...txOptions,
        // don't override with options
        to,
        data,
        value: !!value ? BigInt(value) : undefined,
        chain: SUPPORTED_CHAINS_BY_CHAIN_ID[chainId],
        maxFeePerBlobGas,
        blobs,
      };

      // Note: Rabby.io currently has a race condition when signing EIP-712 objects immediately before transactions.
      // To mitigate this, they recommend setting a timeout before sending the transaction.
      // This workaround can be removed once the issue is resolved:
      // See: https://github.com/RabbyHub/Rabby/issues/2146
      if (connectorId === 'io.rabby' || connectorId === 'com.brave.wallet') {
        await sleep(1000);
      }

      const txHash = await walletClient.sendTransaction(txParams as SendTransactionParameters);

      return txHash;
    },
  );
};
